// @ts-nocheck
import React, { useCallback, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';

import { PageHeader, Button } from '../../components/WorkflowFormPrimitives';
import { useCompanyContext } from '../../context/CompanyContext';
import {
  DEFAULT_PARACHUTE_EQUITY_SUB_ID,
  DEFAULT_PARACHUTE_WORKFLOW_SUB_ID,
  PARACHUTE_WORKFLOW_SUB_ITEMS,
} from '../../parachuteWorkflowSubItems';
import { BuyerEquityAwards } from './BuyerEquityAwards';
import { EquityAwards } from './EquityAwards';
import { EquityAwardsQa24c } from './EquityAwardsQa24c';
import {
  SeveranceContent,
  CICBonusesContent,
  CompIncreasesContent,
  OtherPaymentsContent,
} from './ParachutePaymentSteps';

export const ParachutePayments = ({
  formData,
  setFormData,
  setActiveTab,
  subTab,
  setSubTab,
  equitySubTab,
  setEquitySubTab,
  continueMode,
}) => {
  const {
    isCurrentAnalysisLocked,
    persistBasicsIfChanged,
    persistIndividualsIfChanged,
    persistCompensationIfChanged,
    persistBaseAmountIfChanged,
    persistSeveranceIfChanged,
    persistParachuteJsonSectionsIfChanged,
  } = useCompanyContext();

  const persistDraftForParachute = useCallback(async () => {
    await persistBasicsIfChanged?.(formData);
    await persistIndividualsIfChanged?.(formData);
    await persistCompensationIfChanged?.(formData);
    await persistBaseAmountIfChanged?.(formData);
    await persistSeveranceIfChanged?.(formData);
    await persistParachuteJsonSectionsIfChanged?.(formData);
  }, [
    formData,
    persistBasicsIfChanged,
    persistIndividualsIfChanged,
    persistCompensationIfChanged,
    persistBaseAmountIfChanged,
    persistSeveranceIfChanged,
    persistParachuteJsonSectionsIfChanged,
  ]);

  const isSaveMode = continueMode !== 'dual';
  const forwardNonLastLabel = isCurrentAnalysisLocked ? 'Continue' : 'Save and continue';

  const subTabs = PARACHUTE_WORKFLOW_SUB_ITEMS;
  const currentIndexRaw = subTabs.findIndex((t) => t.id === subTab);
  const displaySubTab = currentIndexRaw >= 0 ? subTab : DEFAULT_PARACHUTE_WORKFLOW_SUB_ID;

  useEffect(() => {
    if (currentIndexRaw === -1) {
      setSubTab(DEFAULT_PARACHUTE_WORKFLOW_SUB_ID);
    }
  }, [currentIndexRaw, setSubTab]);

  useEffect(() => {
    if (displaySubTab !== 'equity') {
      setEquitySubTab(DEFAULT_PARACHUTE_EQUITY_SUB_ID);
    }
  }, [displaySubTab, setEquitySubTab]);

  const currentIndex = currentIndexRaw >= 0 ? currentIndexRaw : 0;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === subTabs.length - 1;
  const prevTab = isFirst ? null : subTabs[currentIndex - 1];
  const nextTab = isLast ? null : subTabs[currentIndex + 1];
  const currentLabel = subTabs[currentIndex]?.label || 'Severance';
  const headerTitle = displaySubTab === 'equity' && equitySubTab === 'qa24c' ? 'Q&A-24(c)' : currentLabel;
  const isQa24cView = displaySubTab === 'equity' && equitySubTab === 'qa24c';

  const handlePrev = () => {
    if (isQa24cView) {
      setEquitySubTab(DEFAULT_PARACHUTE_EQUITY_SUB_ID);
      return;
    }
    if (isFirst) {
      if (continueMode === 'dual') setActiveTab('baseAmount', { persist: false });
      else setActiveTab('baseAmount');
    } else if (prevTab) {
      setSubTab(prevTab.id);
    }
  };

  const prevButtonLabel = isQa24cView ? 'Equity Awards' : isFirst ? 'Base Amount' : prevTab?.label;

  const forwardButtonLabel = (() => {
    if (isLast) return 'View Analysis';
    if (continueMode === 'dual') return 'Continue';
    if (isQa24cView) return 'Continue';
    return forwardNonLastLabel;
  })();
  const handleNext = async () => {
    if (isLast) {
      if (continueMode === 'dual') setActiveTab('analysis', { persist: false });
      else setActiveTab('analysis');
      return;
    }
    if (!nextTab) return;
    if (isSaveMode) {
      try {
        await persistDraftForParachute();
      } catch (e) {
        console.error(e);
        alert('Could not save analysis changes. Please try again.');
        return;
      }
    }
    setSubTab(nextTab.id);
  };

  return (
    <div className="space-y-5">
      <PageHeader title={headerTitle} subtitle={`Step ${currentIndex + 1} of ${subTabs.length} — Parachute Payments`} />
      <div className="flex items-center space-x-1">
        {subTabs.map((t, i) => (
          <div
            key={t.id}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i <= currentIndex ? 'bg-emerald-600' : 'bg-gray-200'}`}
          />
        ))}
      </div>
      <div>
        {displaySubTab === 'severance' && <SeveranceContent formData={formData} setFormData={setFormData} />}
        {displaySubTab === 'cicBonus' && <CICBonusesContent formData={formData} setFormData={setFormData} />}
        {displaySubTab === 'compIncreases' && <CompIncreasesContent formData={formData} setFormData={setFormData} />}
        {displaySubTab === 'equity' && equitySubTab === 'awards' && (
          <EquityAwards formData={formData} setFormData={setFormData} />
        )}
        {displaySubTab === 'equity' && equitySubTab === 'qa24c' && <EquityAwardsQa24c formData={formData} />}
        {displaySubTab === 'buyerEquity' && <BuyerEquityAwards formData={formData} setFormData={setFormData} />}
        {displaySubTab === 'otherPayments' && <OtherPaymentsContent formData={formData} setFormData={setFormData} />}
      </div>
      <div className="flex justify-between items-center pt-2">
        <Button variant="secondary" onClick={handlePrev}>
          <ChevronRight size={14} className="mr-1.5 rotate-180" />
          {prevButtonLabel}
        </Button>
        {continueMode === 'dual' ? (
          <Button variant="secondary" onClick={() => void handleNext()}>
            {forwardButtonLabel}
            <ChevronRight size={14} className="ml-1.5" />
          </Button>
        ) : (
          <Button onClick={() => void handleNext()}>
            {forwardButtonLabel}
            <ChevronRight size={14} className="ml-1.5" />
          </Button>
        )}
      </div>
    </div>
  );
};
